/**
 * @fileoverview "Studio Quiet" UI primitives for the redesigned instructor
 * portal. Real-stack port of the design handoff's `web-ui.jsx`, built on
 * Tailwind + the studio CSS token layer (see src/index.css) and lucide-react.
 *
 * Exports: Icon, Tap, Card, Avatar, Eyebrow, SectionHeader, Button, IconButton,
 * Tag, PulseDot, StatusPill, ListRow, SegmentedControl, StatCard.
 */

import { Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { ICONS } from "@/components/studio/icons";

/**
 * Render a handoff-named icon (see icons.js) as a lucide component.
 * @param {{name:string, size?:number, className?:string, strokeWidth?:number}} props
 */
export function Icon({ name, size = 20, className, strokeWidth = 1.7, ...rest }) {
  const Cmp = ICONS[name];
  if (!Cmp) return <span style={{ width: size, height: size, display: "inline-block" }} />;
  return <Cmp size={size} strokeWidth={strokeWidth} className={cn("shrink-0", className)} {...rest} />;
}

/**
 * Hover-aware tappable surface. Renders a div (so it can safely wrap buttons)
 * with button semantics when `onPress` is provided; lifts 1px on hover.
 */
export function Tap({ onPress, className, children, style, hoverLift = true, ariaLabel, ...rest }) {
  const interactive = !!onPress;
  const handleKey = (e) => {
    if (!interactive) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPress(e); }
  };
  return (
    <div
      onClick={onPress}
      onKeyDown={handleKey}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={ariaLabel}
      className={cn(
        "transition-[background-color,border-color,transform] duration-150",
        interactive && "cursor-pointer",
        interactive && hoverLift && "hover:-translate-y-px",
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Raised card surface. `hover` enables an overlay-bg lift on press. */
export function Card({ children, className, style, onPress, hover = false, padding = 20, ...rest }) {
  return (
    <Tap
      onPress={onPress}
      style={{ padding, ...style }}
      className={cn(
        "rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-raised)]",
        hover && "hover:bg-[var(--studio-overlay)]",
        className,
      )}
      {...rest}
    >
      {children}
    </Tap>
  );
}

/**
 * The column a portal page lays itself out in.
 *
 * `reading` is the prose column most pages use. `wide` is for the detail
 * pages that are mostly tables, tiles and two-column lists — the org program
 * and class pages first — where a reading column left half the screen empty
 * inside the 256px rail. Both keep the same gutter on either side, and a wide
 * page keeps its prose blocks at reading width itself.
 */
export function PageShell({ width = "reading", className, children, ...rest }) {
  return (
    <div
      className={cn(
        "mx-auto w-full px-4 py-8",
        width === "wide" ? "max-w-[1360px] md:px-10" : "max-w-3xl md:px-8",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** Initial-letter avatar circle. `accent` tints the initial yellow. */
export function Avatar({ name = "?", size = 40, accent = false, className }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-interactive)] font-sans font-semibold",
        accent ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-secondary)]",
        className,
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initial}
    </div>
  );
}

/** Micro uppercase section label with optional leading icon. */
export function Eyebrow({ children, icon, className, tone = "tertiary" }) {
  const color = tone === "yellow" ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]";
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {icon && <Icon name={icon} size={13} strokeWidth={1.8} className={color} />}
      <span className={cn("font-sans text-[11px] font-semibold uppercase leading-none tracking-[0.06em]", color)}>
        {children}
      </span>
    </div>
  );
}

/** Section header: title (title3) + optional yellow text action. */
export function SectionHeader({ children, action, onAction, actionTestId, className }) {
  return (
    <div className={cn("mb-4 flex items-center justify-between", className)}>
      <div className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{children}</div>
      {action && (
        <Tap onPress={onAction} hoverLift={false} data-testid={actionTestId} className="font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)]">
          {action}
        </Tap>
      )}
    </div>
  );
}

const BTN_VARIANTS = {
  primary: "bg-[var(--accent-yellow)] text-[var(--text-inverse)] border border-[var(--accent-yellow-edge)]",
  ghost: "bg-[var(--studio-raised)] text-[var(--text-primary)] border border-[var(--studio-divider-strong)]",
  bare: "bg-transparent text-[var(--text-secondary)] border border-[var(--studio-divider)]",
  danger: "bg-transparent text-[var(--state-danger)] border border-[var(--state-danger-border)]",
};

/**
 * Pill button. variants: primary | ghost | bare | danger. sizes: md | lg.
 * Renders a real <button>. Extra props (disabled, type, data-testid…) pass through.
 */
function buttonClass({ variant = "primary", size = "md", fullWidth = false, className }) {
  return cn(
    "inline-flex items-center justify-center gap-[9px] rounded-full font-sans font-semibold tracking-[-0.01em]",
    "transition-[filter,transform,background-color] duration-150 hover:brightness-110 hover:-translate-y-px",
    "disabled:pointer-events-none disabled:opacity-50",
    size === "lg" ? "px-6 py-4 text-base" : "px-[18px] py-[11px] text-[15px]",
    fullWidth && "w-full",
    BTN_VARIANTS[variant] || BTN_VARIANTS.primary,
    className,
  );
}

export function Button({
  children, onPress, onClick, variant = "primary", size = "md",
  icon, iconRight, fullWidth = false, className, ...rest
}) {
  return (
    <button onClick={onPress || onClick} className={buttonClass({ variant, size, fullWidth, className })} {...rest}>
      {icon && <Icon name={icon} size={size === "lg" ? 18 : 17} strokeWidth={2} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "lg" ? 18 : 17} strokeWidth={2} />}
    </button>
  );
}

/**
 * A link that looks and moves exactly like Button: for a door that is an
 * address rather than an action (a mailto, a tel, a page in a new tab), which
 * must stay a real <a> so it opens the mail app or the page. Ghost by default.
 */
export function ButtonLink({ children, href, variant = "ghost", size = "md", icon, iconRight, className, ...rest }) {
  return (
    <a href={href} className={buttonClass({ variant, size, className })} {...rest}>
      {icon && <Icon name={icon} size={size === "lg" ? 18 : 17} strokeWidth={2} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === "lg" ? 18 : 17} strokeWidth={2} />}
    </a>
  );
}

/** Round icon button. `accent` = yellow fill, `danger` = red glyph. */
export function IconButton({ icon, size = 44, onPress, accent = false, danger = false, className, ariaLabel, ...rest }) {
  return (
    <button
      onClick={onPress}
      aria-label={ariaLabel}
      className={cn(
        "flex items-center justify-center rounded-full border border-[var(--studio-divider)] transition-colors duration-150",
        accent ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-raised)] hover:bg-[var(--studio-interactive)]",
        className,
      )}
      style={{ width: size, height: size }}
      {...rest}
    >
      <Icon
        name={icon}
        size={Math.round(size * 0.42)}
        strokeWidth={1.8}
        className={danger ? "text-[var(--state-danger)]" : accent ? "text-[var(--text-inverse)]" : "text-[var(--text-secondary)]"}
      />
    </button>
  );
}

/** Small chip/tag. `accent` = yellow-dim. */
export function Tag({ children, accent = false, className, ...rest }) {
  return (
    <span
      {...rest}
      className={cn(
        "inline-flex items-center gap-[5px] rounded-full px-[11px] py-[5px] font-sans text-[13px] font-medium",
        accent
          ? "border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--accent-yellow-text)]"
          : "border border-[var(--studio-divider)] bg-[var(--studio-interactive)] text-[var(--text-secondary)]",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Expanding pulse dot (yellow). */
const PULSE_TONES = {
  accent: { dot: "bg-[var(--accent-yellow)]", ring: "border-[var(--accent-yellow)]" },
  success: { dot: "bg-[var(--state-success)]", ring: "border-[var(--state-success)]" },
};

/**
 * A small live-status dot with a breathing halo. `tone` defaults to accent so
 * existing callers are unchanged; "success" is the green used for a program
 * you currently teach in.
 */
export function PulseDot({ className, tone = "accent" }) {
  const t = PULSE_TONES[tone] || PULSE_TONES.accent;
  return (
    <span className={cn("relative inline-block h-2 w-2", className)}>
      <span className={cn("absolute inset-0 rounded-full", t.dot)} />
      <span className={cn("absolute -inset-[3px] rounded-full border [animation:hansPulse_1.8s_ease-out_infinite]", t.ring)} />
    </span>
  );
}

const STATUS_MAP = {
  Granted: { icon: "shield", cls: "text-[var(--state-success)] bg-[var(--state-success-dim)] border-[var(--state-success-border)]" },
  Pending: { icon: "clock", cls: "text-[var(--accent-yellow-text)] bg-[var(--accent-yellow-dim)] border-[var(--accent-yellow-border)]" },
  Sent: { icon: "check", cls: "text-[var(--state-success)] bg-[var(--state-success-dim)] border-[var(--state-success-border)]" },
  Declined: { icon: "x", cls: "text-[var(--state-danger)] bg-[var(--state-danger-dim)] border-[var(--state-danger-border)]" },
  // Practice assignments (Spec 01)
  Assigned: { icon: "target", cls: "text-[var(--accent-yellow-text)] bg-[var(--accent-yellow-dim)] border-[var(--accent-yellow-border)]" },
  Completed: { icon: "check", cls: "text-[var(--state-success)] bg-[var(--state-success-dim)] border-[var(--state-success-border)]" },
  Closed: { icon: "clock", cls: "text-[var(--text-tertiary)] bg-[var(--studio-interactive)] border-[var(--studio-divider-strong)]" },
};

/**
 * Generic status pill (Granted | Pending | Sent | Declined). For consent
 * specifically prefer <ConsentBadge> which maps consent_status values.
 */
export function StatusPill({ kind, className }) {
  const m = STATUS_MAP[kind] || STATUS_MAP.Pending;
  return (
    <span className={cn("inline-flex items-center gap-[5px] rounded-full border px-[10px] py-1 font-sans text-xs font-semibold", m.cls, className)}>
      <Icon name={m.icon} size={12} strokeWidth={2} /> {kind}
    </span>
  );
}

/**
 * List row (lessons, resources, students). Optional `onRecord` renders a quiet
 * mic shortcut revealed on row hover/focus — never a persistent yellow button.
 */
export function ListRow({ leading, title, subtitle, meta, trailing, onPress, onRecord, recordLabel, borderBottom, wrapText = false, className, ...rest }) {
  return (
    <div
      className={cn(
        "group relative flex items-center gap-[14px] px-[18px] py-[14px]",
        borderBottom && "border-b border-[var(--studio-divider)]",
        onPress && "cursor-pointer transition-colors duration-150 hover:bg-[var(--studio-overlay)]",
        className,
      )}
      onClick={onPress}
      onKeyDown={onPress ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPress(e); } } : undefined}
      role={onPress ? "button" : undefined}
      tabIndex={onPress ? 0 : undefined}
      {...rest}
    >
      {leading}
      <div className="min-w-0 flex-1">
        <div className={cn(wrapText ? "break-words whitespace-normal" : "truncate", "font-sans text-base font-medium text-[var(--text-primary)]")}>{title}</div>
        {subtitle && <div className={cn(wrapText ? "break-words whitespace-normal" : "truncate", "mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]")}>{subtitle}</div>}
      </div>
      {meta && <span className="font-mono text-[13px] text-[var(--text-tertiary)]">{meta}</span>}
      {onRecord && (
        <button
          type="button"
          aria-label={recordLabel || "Record lesson"}
          onClick={(e) => { e.stopPropagation(); onRecord(e); }}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] opacity-0 transition-opacity duration-150 hover:bg-[var(--studio-interactive)] hover:text-[var(--accent-yellow-text)] focus:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Mic size={16} strokeWidth={1.8} />
        </button>
      )}
      {trailing}
    </div>
  );
}

/**
 * Segmented control (Students/Classes, Metronome/Tuner). options: [{id,label,count?}].
 * `block` makes segments flex-1 with a fixed height (Tools style).
 */
export function SegmentedControl({ options, value, onChange, block = false, className, testId }) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "inline-flex gap-1 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-1",
        block && "flex w-full rounded-[14px] p-[5px]",
        className,
      )}
    >
      {options.map((o) => {
        const sel = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={o.testId}
            className={cn(
              "flex items-center justify-center gap-2 font-sans font-semibold transition-colors duration-150",
              block ? "h-[46px] flex-1 rounded-[10px] text-base capitalize" : "rounded-full px-[22px] py-[9px] text-[15px]",
              sel ? "bg-[var(--accent-yellow)] text-[var(--text-inverse)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            )}
          >
            {o.label}
            {o.count != null && (
              <span
                className={cn(
                  "rounded-md px-[7px] py-px text-[10px] font-semibold",
                  sel ? "bg-[rgba(0,0,0,0.2)] text-[var(--text-inverse)]" /* brand-lint-ignore: ink on the yellow fill, same in both themes */ : "bg-[var(--studio-interactive)] text-[var(--text-tertiary)]",
                )}
              >
                {o.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Quiet text tabs: a filter that sits UNDER a SegmentedControl without
 * repeating it. Plain words on a hairline, the chosen one underlined in the
 * accent, counts as small grey numerals. Same options shape as
 * SegmentedControl ({id, label, count?, testId?}), plus `disabled?` for a
 * choice that is not available yet.
 */
export function TextTabs({ options, value, onChange, className, testId }) {
  return (
    <div
      role="tablist"
      data-testid={testId}
      className={cn("flex gap-6 border-b border-[var(--studio-divider)]", className)}
    >
      {options.map((o) => {
        const sel = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={sel}
            disabled={o.disabled}
            onClick={() => onChange(o.id)}
            data-testid={o.testId}
            className={cn(
              "-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 pt-1 font-sans text-[14px] transition-colors duration-150",
              "disabled:pointer-events-none disabled:opacity-50",
              sel
                ? "border-[var(--accent-yellow)] font-semibold text-[var(--text-primary)]"
                : "border-transparent font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)]",
            )}
          >
            {o.label}
            {o.count != null && <span className="font-mono text-[12px] font-normal text-[var(--text-tertiary)]">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Quick-glance stat card: a big mono value as the hero, with its label and
 * caption anchored to the bottom. Icon-less by design — the number carries the
 * tile, so it gets room to breathe instead of being wedged under an icon.
 * Clickable when `onPress` is set (shows a chevron). Padding rides `style`
 * (Card's inline padding overrides `p-*` classes — see the Card padding contract).
 * An optional `action`/`onAction` pair renders a small "+" pill anchored to the
 * bottom-right corner (absolute, so the card's size/shape never changes); its
 * clicks/keys stop propagating so they never trigger the card's own `onPress`.
 */

/**
 * A number and what it is. `compact` is the row of vital signs that sits
 * between a page's title and its tab bar: shorter, so four of them do not
 * push the tab content below the fold, and a link into the tab that holds
 * the rows behind the number when `onPress` is given.
 */
export function StatCard({ value, label, sub, onPress, action, onAction, actionTestId, className, compact = false, ...rest }) {
  return (
    <Card
      hover={!!onPress}
      onPress={onPress}
      className={cn(
        "relative flex flex-col",
        // Compact tiles sit in a row that stretches to its tallest member;
        // top-aligned content keeps a tile without a sub-line from opening a
        // gap between its number and its label.
        compact ? "justify-start" : "min-h-[118px] justify-between",
        className,
      )}
      style={{ padding: compact ? "14px 16px" : "22px 24px" }}
      {...rest}
    >
      {onPress && (
        <Icon
          name="chevronRight"
          size={compact ? 15 : 18}
          className={cn("absolute text-[var(--text-tertiary)]", compact ? "right-3.5 top-3.5" : "right-5 top-5")}
        />
      )}
      <div
        className={cn(
          "font-mono font-light leading-none tracking-[-0.01em] text-[var(--text-primary)]",
          compact ? "text-[26px]" : "text-[40px]",
        )}
      >
        {value}
      </div>
      <div className={compact ? "mt-1.5" : "mt-4"}>
        <div className={cn("font-sans font-medium text-[var(--text-primary)]", compact ? "text-[13px]" : "text-base")}>{label}</div>
        {sub && <div className={cn("mt-0.5 font-sans text-[var(--text-tertiary)]", compact ? "text-[12px]" : "text-[13px]")}>{sub}</div>}
      </div>
      {action && (
        <button
          type="button"
          data-testid={actionTestId}
          onClick={(e) => { e.stopPropagation(); onAction?.(e); }}
          onKeyDown={(e) => e.stopPropagation()}
          className="absolute bottom-5 right-5 flex items-center gap-1 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] py-1.5 pl-2.5 pr-3 font-sans text-[12px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--studio-interactive)] hover:text-[var(--text-primary)]"
        >
          <Icon name="plus" size={13} strokeWidth={2} />
          {action}
        </button>
      )}
    </Card>
  );
}
